"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { File, Download, Trash, AlertTriangle, Loader2 } from "lucide-react";
import {
  useDeleteFileMutation,
  useGetFilesQuery,
  useSignedUrlMutation,
} from "@/services/file";
import { toast } from "@/components/ui/use-toast";
import { Card, CardContent } from "@/components/ui/card";

export default function DownloadEvidence({
  id,
  type,
  shouldBackTo,
  isClosed,
}: {
  id: number;
  type?: string;
  shouldBackTo?: () => void;
  isClosed: boolean,
}) {
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const { data, isLoading } = useGetFilesQuery({
    entity_name: type ?? "divergence",
    id: id.toString(),
  });

  const [signedUrl, { isLoading: isDownloading }] = useSignedUrlMutation();
  const [deleteFile, { isLoading: isDeleting }] = useDeleteFileMutation();

  const handleDownload = async (fileId: number) => {
    try {
      const response = await signedUrl({ id: fileId.toString() }).unwrap();
      window.open(response.url);
    } catch (error) {
      console.error("Error downloading file:", error);
      toast({
        title: "Erro",
        description: "Não foi possível baixar o arquivo.",
        variant: "destructive",
      });

      throw new Error(
        `Error: ${JSON.stringify(
          (error as { data?: { messages?: string[] } })?.data?.messages
        )}`
      );
    }
  };

  const openDeleteDialog = (fileId: string) => {
    setFileToDelete(fileId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!fileToDelete) return;

    try {
      await deleteFile({ id: fileToDelete }).unwrap();
      toast({
        title: "Sucesso",
        description: "Arquivo excluído com sucesso.",
        variant: "success",
      });
    } catch (error) {
      console.error("Error deleting file:", error);
      toast({
        title: "Error",
        description: "Não foi possível excluir o arquivo.",
        variant: "destructive",
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setFileToDelete(null);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full h-[300px] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  const files = data?.data || [];

  return (
    <div className="flex flex-col gap-4 w-full">
      <Card className="w-full">
        <CardContent className="p-0">
          <ScrollArea className="h-[300px] w-full rounded-md p-4">
            {files.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-4">
                <File className="h-12 w-12 text-muted-foreground mb-2" />
                <p className="text-muted-foreground">
                  Nenhuma evidência encontrada
                </p>
              </div>
            ) : (
              <ul className="space-y-3">
                {files.map((file: any) => (
                  <li
                    key={file.id}
                    className="flex items-center space-x-4 p-3 rounded-md hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                      <File className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium leading-none truncate mb-1">
                        {file.file_name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {file.file_type} • {type ?? "Divergência"}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(file.id)}
                        disabled={isDownloading || isClosed}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Baixar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openDeleteDialog(file.id)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                        disabled={isDeleting || isClosed}
                      >
                        <Trash className="h-4 w-4 mr-2" />
                        Excluir
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {shouldBackTo && (
        <div className="flex justify-start mt-2">
          <Button
            onClick={shouldBackTo}
            className="max-w-[100px]"
          >
            Voltar
          </Button>
        </div>
      )}

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Confirmar exclusão
            </DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir este arquivo? Esta ação não pode
              ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                <>
                  <Trash className="h-4 w-4 mr-2" />
                  Excluir
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
